import { useEffect, useMemo, useState } from 'react';
import {
  activityOptionsForUsers,
  pruneIds,
  UserActivityFilterOptionsDTO,
  UserActivitySortFilterApply,
  UserActivitySortOrder,
} from '../../../UserActivity.meta';
import { resetActivitySelection, shouldPruneActivitySelection } from '../SortFilter.meta';
import { useFilterDraft } from './useFilterDraft';

interface Params {
  filterOptions: UserActivityFilterOptionsDTO;
  sortOrder: UserActivitySortOrder;
  selectedActivityFilterIds: string[];
  selectedUserFilterIds: string[];
  onApplyChange: (change: UserActivitySortFilterApply) => void;
  onOpenChange: (isOpen: boolean) => void;
}

export const useSortFilterDraft = ({
  filterOptions,
  sortOrder,
  selectedActivityFilterIds,
  selectedUserFilterIds,
  onApplyChange,
  onOpenChange,
}: Params) => {
  const [draftSortOrder, setDraftSortOrder] = useState(sortOrder);

  useEffect(() => {
    setDraftSortOrder(sortOrder);
  }, [sortOrder]);

  const userFilter = useFilterDraft(selectedUserFilterIds);
  const activityFilter = useFilterDraft(selectedActivityFilterIds);

  const { activityOptionsByUserId, userOptions } = filterOptions;
  const showUserColumn = userOptions.length > 0;

  // While the user filter has debounced search text, effectiveIds is search-scoped; activity
  // types available in the activity column follow the scoped users once search settles.
  const activityOptions = useMemo(
    () => activityOptionsForUsers(activityOptionsByUserId, userFilter.effectiveIds),
    [activityOptionsByUserId, userFilter.effectiveIds],
  );
  const showActivityColumn = activityOptions.length > 0;

  // When the scoped user set changes, sync activity draft selection to the new option list.
  // Prune when draft still matches applied filters; otherwise select all activities for the
  // scoped users (e.g. user search narrows the list and draft user ids diverge from applied).
  useEffect(() => {
    const availableIds = activityOptions.map(option => option.id);

    if (
      shouldPruneActivitySelection(
        selectedActivityFilterIds,
        activityFilter.selectedIds,
        selectedUserFilterIds,
        userFilter.selectedIds,
      )
    ) {
      activityFilter.setSelectedIds(prev => pruneIds(prev, availableIds));
      activityFilter.setSearchSelectedIds(prev => pruneIds(prev, availableIds));
    } else {
      const reset = resetActivitySelection(availableIds);
      activityFilter.setSelectedIds(reset.selectedIds);
      activityFilter.setSearchSelectedIds(reset.searchSelectedIds);
    }
  }, [activityOptions, selectedActivityFilterIds, selectedUserFilterIds]);

  useEffect(() => {
    activityFilter.setSearchTerm('');
  }, [userFilter.effectiveIds]);

  const isApplyValid =
    (!showUserColumn || userFilter.effectiveIds.length > 0) &&
    (!showActivityColumn || activityFilter.effectiveIds.length > 0);
  const isUnSelectValid =
    (showUserColumn && userFilter.effectiveIds.length > 0) ||
    (showActivityColumn && activityFilter.effectiveIds.length > 0);

  const handleApply = () => {
    onApplyChange({
      sortOrder: draftSortOrder,
      selectedActivityFilterIds: [...activityFilter.effectiveIds],
      selectedUserFilterIds: [...userFilter.effectiveIds],
    });
    onOpenChange(false);
  };

  const handleUnselectAll = () => {
    userFilter.reset();
    activityFilter.reset();
  };

  return {
    draftSortOrder,
    setDraftSortOrder,
    showUserColumn,
    showActivityColumn,
    userOptions,
    activityOptions,
    userFilter,
    activityFilter,
    isApplyValid,
    isUnSelectValid,
    handleApply,
    handleUnselectAll,
  };
};
